import { useEffect, useMemo, useState } from 'react'

import {
  CepIntegration,
  PayloadCepIntegration,
  TestingMethodology,
} from 'common/api'
import ActionIcon from 'common/components/ActionIcon'
import CepIcon from 'common/components/cepIcon/CepIcon'
import { getIconForDistributionChannel } from 'common/components/channelPicker/ChannelPicker'
import ConfirmationModal from 'common/components/confirmationModal'
import LoadingOverlay from 'common/components/LoadingOverlay'
import { Column } from 'common/components/table'
import BaseCell from 'common/components/table/cells/Base'
import TableWidget from 'common/components/widget/tableWidget'
import { formatPastDate } from 'common/helpers/date'
import { useAppSelector } from 'common/hooks/redux'
import { Bin, Edit } from 'common/icons'
import { DistributionChannel } from 'common/interfaces/projects'

import useDeleteCepIntegration from '../../api/mutations/useDeleteCepIntegration'
import useUpdateCepIntegration from '../../api/mutations/useUpdateCepIntegration'
import useGetCepIntegrationTemplatesQuery, {
  CepTemplateFormatted,
} from '../../api/queries/useGetCepIntegrationTemplatesQuery'
import useGetIntegrationByIdQuery from '../../api/queries/useGetIntegrationByIdQuery'

import CreateCepIntegrationModal from './createCepIntegrationWidget/CreateCepIntegrationModal'

type Props = {
  accounts: CepIntegration[]
  onUpdate: (integrationAccount: CepIntegration) => void
  onDelete: (integrationAccount: CepIntegration) => void
}

const formatTestingMethodology = (testingMethodology: TestingMethodology) => {
  switch (testingMethodology) {
    case 'test_and_send':
      return 'Test and send'
    case 'dynamic_batch':
      return 'Dynamic - batch'
    case 'dynamic_realtime':
      return 'Dynamic - realtime'
    case 'straight_splits':
      return 'Straight split'
    default:
      return testingMethodology
  }
}

const AccountsListCepIntegration = ({ accounts }: Props) => {
  let cepTemplate: CepTemplateFormatted | undefined = undefined
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const { mutate: deleteCepIntegration, isLoading: isDeletingCepIntegration } =
    useDeleteCepIntegration()
  const { mutate: updateCepIntegration, isLoading: isUpdatingCepIntegration } =
    useUpdateCepIntegration()
  const { data: cepTemplates } = useGetCepIntegrationTemplatesQuery(
    accountId,
    true
  )
  const [selectedIntegration, setSelectedIntegration] =
    useState<CepIntegration | undefined>(undefined)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [integrationToDelete, setIntegrationToDelete] =
    useState<CepIntegration | undefined>(undefined)
  const [isCreateWidgetVisible, setIsCreateWidgetVisible] =
    useState<boolean>(false)
  const columns: Column<CepIntegration>[] = useMemo(
    () => [
      {
        Header: 'Integration Name',
        accessor: 'name',
        className: 'uppercase',
        Cell: ({ value, row }) => (
          <BaseCell>
            <span className="capitalize">{`${value}`}</span>
          </BaseCell>
        ),
        width: 200,
      },
      {
        Header: 'Cep',
        accessor: 'cep_name',
        className: 'uppercase',
        Cell: ({ value }) => (
          <BaseCell>
            <CepIcon name={value} style={{ height: '35px', width: '35px' }} />
          </BaseCell>
        ),
        width: 100,
      },
      {
        Header: 'Channel',
        accessor: 'distribution_channel',
        className: 'uppercase',
        Cell: ({ value }) => {
          const Icon = getIconForDistributionChannel(
            value as DistributionChannel
          )
          return <BaseCell>{Icon && <Icon size={6} />}</BaseCell>
        },
        width: 80,
      },
      {
        Header: 'Message Type',
        accessor: 'message_type',
        className: 'uppercase',
        Cell: ({ value }) => <BaseCell>{value}</BaseCell>,
        width: 100,
      },
      {
        Header: 'Testing Methodology',
        accessor: 'testing_methodology',
        className: 'uppercase',
        Cell: ({ value }) => (
          <BaseCell>{formatTestingMethodology(value)}</BaseCell>
        ),
        width: 200,
      },
      {
        Header: 'Created',
        accessor: 'created_at',
        Cell: ({ value }) => (
          <BaseCell className="capitalize">{formatPastDate(value)}</BaseCell>
        ),
        align: 'right',
        width: 100,
        className: 'uppercase',
      },
      {
        Header: 'Actions',
        accessor: '_id',
        align: 'right',
        width: 70,
        disableSortBy: true,
        className: 'uppercase',
        Cell: ({ row }) => (
          <BaseCell className="w-full justify-end space-x-4">
            <ActionIcon
              onClick={() => {
                setIsDeleteModalVisible(true)
                setIntegrationToDelete(row.original)
              }}
            >
              <Bin
                isDefaultColor={false}
                className="text-base-700 hover:text-maroon-600"
              />
            </ActionIcon>

            <ActionIcon
              onClick={() => {
                if (selectedIntegration?._id !== row.original._id) {
                  setSelectedIntegration(row.original)
                }
                setIsCreateWidgetVisible(true)
              }}
            >
              <Edit
                isDefaultColor={false}
                className="text-base-700 hover:text-maroon-600"
              />
            </ActionIcon>
          </BaseCell>
        ),
      },
    ],
    [selectedIntegration?._id]
  )

  const { data: integration, isFetching: isFecthingIntegrationById } =
    useGetIntegrationByIdQuery(selectedIntegration?._id)

  useEffect(() => {
    if (integration?.credentials) {
      setSelectedIntegration((prev) =>
        prev
          ? {
              ...prev,
              credentials: integration.credentials,
            }
          : prev
      )
    }
  }, [integration])

  cepTemplate = useMemo(() => {
    if (!cepTemplates || !selectedIntegration || !integration) {
      return undefined
    }
    return cepTemplates.find(
      (cep) => cep.cepName === selectedIntegration.cep_name
    )
  }, [cepTemplates, selectedIntegration, integration])

  return (
    <>
      <TableWidget.Widget columns={columns} data={accounts} />
      <ConfirmationModal
        data-cy="delete-confirmation-modal"
        data-testid="delete-confirmation-modal"
        title="Delete this integration?"
        confirmationText="This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        open={isDeleteModalVisible}
        hasConfirmationSlider
        onConfirm={() => {
          deleteCepIntegration(integrationToDelete?._id!)
          setIsDeleteModalVisible(false)
          setIntegrationToDelete(undefined)
        }}
        onCancel={() => {
          setIsDeleteModalVisible(false)
          setIntegrationToDelete(undefined)
        }}
      />
      {cepTemplate && (
        <CreateCepIntegrationModal
          cep={cepTemplate}
          integrationToUpdate={selectedIntegration}
          isCreateWidgetVisible={isCreateWidgetVisible}
          onClose={() => {
            setIsCreateWidgetVisible(false)
          }}
          onSubmit={async (payload: PayloadCepIntegration) => {
            updateCepIntegration({
              payload,
              integrationId: selectedIntegration?._id!,
            })
            setSelectedIntegration(undefined)
          }}
        />
      )}
      <LoadingOverlay
        isLoading={
          isFecthingIntegrationById ||
          isUpdatingCepIntegration ||
          isDeletingCepIntegration
        }
        phrases={['Loading ...']}
      />
    </>
  )
}

export default AccountsListCepIntegration
